import React, { FC, ReactNode } from 'react';
import { useModalContext } from '@lib/modalContext';
import AnalyticsServiceMomentsModal from './AnalyticsServiceMomentsModal';
import AddMomentsToContentModal from './AddMomentsToContentModal';
import DeleteMomentTemplateModal from './DeleteMomentTemplateModal';
import DisplayMomentDetailsModal from './DisplayMomentDetailsModal';
import OpenPreviewServiceModal from './OpenPreviewServiceModal';
import RiskModal from './RiskModal';
import { ServiceDataMoment } from '../Analytics/Service/AnalyticsServiceGrid';
import { MomentScheduleItemFieldsFragment, MomentType } from '@generated/types';
import DeleteNextStepsTemplateModal from './DeleteNextStepsTemplateModal';

export enum ModalType {
  DELETE_MOMENT_TEMPLATE = 'DELETE_MOMENT_TEMPLATE',
  DELETE_NEXT_STEPS_TEMPLATE = 'DELETE_NEXT_STEPS_TEMPLATE',
  ADD_MOMENTS_TO_CONTENT = 'ADD_MOMENTS_TO_CONTENT',
  ANALYTICS_SERVICE_MOMENT_METRICS = 'ANALYTICS_SERVICE_MOMENT_METRICS',
  ANALYTICS_SERVICE_MOMENT_DETAIL_METRICS = 'ANALYTICS_SERVICE_MOMENT_DETAIL_METRICS',
  RISK_MODAL = 'RISK_MODAL',
  OPEN_PREVIEW_SERVICE = 'OPEN_PREVIEW_SERVICE',
}

interface OpenPreviewServiceModal {
  type: ModalType.OPEN_PREVIEW_SERVICE;
}
interface DeleteMomentTemplate {
  type: ModalType.DELETE_MOMENT_TEMPLATE;
  data: {
    id: string;
  };
}

interface DeleteNextStepsTemplate {
  type: ModalType.DELETE_NEXT_STEPS_TEMPLATE;
  data: {
    id: string;
  };
}

interface AnalyticsServiceMomentsModal {
  type: ModalType.ANALYTICS_SERVICE_MOMENT_METRICS;
  data: {
    moments: ServiceDataMoment[];
  };
}

interface DisplayMomentDetailsModal {
  type: ModalType.ANALYTICS_SERVICE_MOMENT_DETAIL_METRICS;
  data: {
    moment: ServiceDataMoment;
  };
}

interface AddMomentsToContent {
  type: ModalType.ADD_MOMENTS_TO_CONTENT;
  data: {
    addMomentScheduleItems: (
      addMomentScheduleItems: Array<MomentScheduleItemFieldsFragment>
    ) => void;
    momentScheduleItems: Array<MomentScheduleItemFieldsFragment>;
    options: {
      [MomentType.PRAYER]: {
        isLivePrayerEnabled: boolean;
      };
    };
  };
}

interface Risk {
  type: ModalType.RISK_MODAL;
  data: {
    body: ReactNode;
    buttonText: string;
    callback: () => void;
  };
}

type Modal =
  | DeleteMomentTemplate
  | DeleteNextStepsTemplate
  | AddMomentsToContent
  | AnalyticsServiceMomentsModal
  | DisplayMomentDetailsModal
  | OpenPreviewServiceModal
  | Risk;

interface Props {
  modal: Modal;
}

const Modal: FC<Props> = ({ modal }) => {
  const { closeModal } = useModalContext();
  switch (modal.type) {
    case ModalType.ADD_MOMENTS_TO_CONTENT:
      return (
        <AddMomentsToContentModal
          addMomentScheduleItems={modal.data.addMomentScheduleItems}
          dismiss={closeModal}
          momentScheduleItems={modal.data.momentScheduleItems}
          options={modal.data.options}
        />
      );
    case ModalType.ANALYTICS_SERVICE_MOMENT_DETAIL_METRICS:
      return <DisplayMomentDetailsModal dismiss={closeModal} moment={modal.data.moment} />;
    case ModalType.ANALYTICS_SERVICE_MOMENT_METRICS:
      return <AnalyticsServiceMomentsModal dismiss={closeModal} moments={modal.data.moments} />;
    case ModalType.DELETE_MOMENT_TEMPLATE:
      return <DeleteMomentTemplateModal dismiss={closeModal} id={modal.data.id} />;
    case ModalType.DELETE_NEXT_STEPS_TEMPLATE:
      return <DeleteNextStepsTemplateModal dismiss={closeModal} id={modal.data.id} />;
    case ModalType.OPEN_PREVIEW_SERVICE:
      return <OpenPreviewServiceModal dismiss={closeModal} />;
    case ModalType.RISK_MODAL:
      return (
        <RiskModal
          body={modal.data.body}
          buttonText={modal.data.buttonText}
          callback={modal.data.callback}
          closeModal={closeModal}
        />
      );
    default:
      return null;
  }
};

export default Modal;
